/**
 * Page Performance — DataTable column sets for the Pages table (and its
 * Overview cut) and the Companies table.
 *
 * Column rules from the spec:
 *   - figures lead with their plain name; the code stays in the header
 *     string ("Typical open (P50)") so the sort control, the column manager
 *     and every export carry it, and a short meaning sits on a second,
 *     screen-only line (`subheader`);
 *   - the name column has a stated width and clips its INNER lines;
 *   - the Status column is pinned to the right edge, so the verdict is never
 *     the column past the edge of a scrolled table;
 *   - every figure carries its unit on screen.
 * Column keys are the server's field names, so a header click sorts on the
 * server by that field.
 */
import { Eye, Filter } from 'lucide-react'
import { formatDateTime, formatNumber } from '../../lib/format'
import {
  STATUS_LABEL,
  countOf,
  formatMs,
  formatPercent,
  formatWaited,
  pageNameForRoute,
  relativeTime,
  trendHeaderNote,
  trendInWords,
} from '../../lib/page-perf/view-model'
import { ErrorsCell, Muted, P95Cell, PageNameCell, PerfStatusBadge, TrendCell } from './PerfUi'

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const exportMs = (v) => (num(v) == null ? '' : Math.round(v))


/** The Errors column tooltip (Pages and Companies tables). */
const ERRORS_TITLE = 'The share of opens in which at least one API call failed: a server error (5xx), a timeout (408), too many requests (429) or no reply — a permission or not-found reply (403, 404 …) does not count.'

function msColumn(key, header, subheader, headerTitle, extra = {}) {
  return {
    key,
    header,
    subheader,
    headerTitle,
    align: 'right',
    minWidth: 120,
    sortAccessor: (r) => num(r[key]) ?? -1,
    exportValue: (r) => exportMs(r[key]),
    cellClassName: 'tabular-nums text-[color:var(--color-ink-muted)] whitespace-nowrap',
    render: (r) => (num(r[key]) == null ? <Muted>—</Muted> : formatMs(r[key])),
    ...extra,
  }
}

/**
 * Pages table columns. `variant: 'overview'` is the Overview's slowest-pages
 * cut, which drops the columns the Overview does not exist to answer
 * (recency, TTFB/LCP detail, P50/P99, opens left early).
 */
export function buildPageColumns({ slot, limits, onOpen, onInfo, variant = 'full' }) {
  const full = variant === 'full'
  const cols = [
    {
      key: 'route',
      header: 'Page',
      subheader: 'the app’s name · the route',
      minWidth: 220,
      pinnable: true,
      sortAccessor: (r) => r.route,
      searchAccessor: (r) => `${pageNameForRoute(r.route)} ${r.route}`,
      exportValue: (r) => `${pageNameForRoute(r.route)} (${r.route})`,
      render: (r) => <PageNameCell route={r.route} />,
    },
    {
      key: 'trend',
      header: 'Trend',
      subheader: trendHeaderNote(slot),
      headerTitle: 'The slowest 5 of 100 opens (P95) in each slot of the period.',
      sortable: false,
      minWidth: 130,
      exportValue: (r) => {
        const t = trendInWords(r.trend, slot)
        return t.sub ? `${t.text} · ${t.sub}` : t.text
      },
      render: (r) => <TrendCell trend={r.trend} slot={slot} status={r.status} />,
    },
    {
      key: 'loads',
      header: 'Page loads',
      subheader: 'opens · people · time waited',
      headerTitle: 'How many times the page was opened, by how many people, and every open’s load time added together (average × opens).',
      align: 'right',
      minWidth: 150,
      sortAccessor: (r) => num(r.loads) ?? 0,
      exportValue: (r) => num(r.loads) ?? 0,
      render: (r) => (
        <div className="inline-flex flex-col items-end text-right">
          <b className="tabular-nums text-[color:var(--color-ink-strong)]">{formatNumber(num(r.loads) ?? 0)}</b>
          {num(r.users) != null ? <span className="text-xs text-[color:var(--color-ink-muted)]">{countOf(r.users, 'person', 'people')}</span> : null}
          <span className="whitespace-nowrap text-xs text-[color:var(--color-ink-muted)]" title="All the opens’ load times added together (average × opens)">
            ⏱ <b>{formatWaited(num(r.waitedMs) ?? (num(r.avgMs) != null && num(r.loads) != null ? r.avgMs * r.loads : null))}</b> waited in total
          </span>
        </div>
      ),
    },
    msColumn('avgMs', 'Average open', 'Avg · all opens ÷ opens', 'Every open’s time added up, divided by the number of opens.'),
  ]
  if (full) {
    cols.push(msColumn('p50Ms', 'Typical open (P50)', 'half were faster', 'Half of the opens were faster than this — what most people feel.'))
  }
  cols.push({
    key: 'p95Ms',
    header: 'Slowest 5 of 100 (P95)',
    subheader: 'the colour uses this',
    headerTitle: '95 of every 100 opens finished within this time. The page’s colour is judged on it.',
    align: 'right',
    minWidth: 160,
    sortAccessor: (r) => num(r.p95Ms) ?? -1,
    exportValue: (r) => exportMs(r.p95Ms),
    render: (r) => <P95Cell row={r} limits={limits} onInfo={onInfo} />,
  })
  if (full) {
    cols.push(
      msColumn('p99Ms', 'Slowest 1 of 100 (P99)', 'the worst opens', '99 of every 100 opens finished within this — the worst case.'),
      msColumn('ttfbP95Ms', 'TTFB (P95)', 'first byte · full loads', 'Time To First Byte, slowest 5 of 100 full loads. In-app navigations have no TTFB.'),
      msColumn('lcpP75Ms', 'LCP (P75)', 'main content · full loads', 'Largest Contentful Paint, 3 of 4 full loads within this.'),
    )
  }
  cols.push({
    key: 'failedRate',
    header: 'Errors',
    subheader: 'opens with a failed request',
    headerTitle: ERRORS_TITLE,
    align: 'right',
    minWidth: 130,
    sortAccessor: (r) => num(r.failedRate) ?? -1,
    exportValue: (r) => (num(r.failedRate) == null ? '' : formatPercent(r.failedRate)),
    render: (r) => <ErrorsCell value={r.failedRate} limits={limits} />,
  })
  if (full) {
    cols.push(
      {
        key: 'abandoned',
        header: 'Left early',
        subheader: 'opens left before they finished',
        headerTitle: 'Opens the person left before the page settled. Not included in the averages or percentiles.',
        align: 'right',
        minWidth: 130,
        sortAccessor: (r) => num(r.abandoned) ?? 0,
        exportValue: (r) => num(r.abandoned) ?? '',
        cellClassName: 'tabular-nums text-[color:var(--color-ink-muted)]',
        render: (r) =>
          num(r.abandoned) == null ? <Muted>—</Muted> : (
            <span title={`${countOf(r.abandoned, 'open')} left before ${r.abandoned === 1 ? 'it' : 'they'} finished`}>{formatNumber(r.abandoned)}</span>
          ),
      },
      {
        key: 'lastOpenedAt',
        header: 'Last opened',
        minWidth: 130,
        sortAccessor: (r) => (r.lastOpenedAt ? new Date(r.lastOpenedAt) : null),
        exportValue: (r) => (r.lastOpenedAt ? formatDateTime(r.lastOpenedAt) : ''),
        cellClassName: 'text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (r) => <span title={formatDateTime(r.lastOpenedAt)}>{relativeTime(r.lastOpenedAt)}</span>,
      },
    )
  }
  cols.push({
    key: 'status',
    header: 'Status',
    pinnedRight: true,
    minWidth: 140,
    sortable: false,
    exportValue: (r) => STATUS_LABEL[r.status] ?? 'Not judged',
    render: (r) => (
      <span className="inline-flex items-center gap-1.5">
        <PerfStatusBadge status={r.status} />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onOpen(r.route)
          }}
          aria-label={`Open ${pageNameForRoute(r.route)} (${r.route})`}
          title="View page"
          className="inline-flex min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-accent-strong)]"
        >
          <Eye size={15} />
        </button>
      </span>
    ),
  })
  return cols
}

/** Companies table columns (all-companies view). */
export function buildCompanyColumns({ slot, limits, onNarrow }) {
  return [
    {
      key: 'name',
      header: 'Company',
      minWidth: 220,
      pinnable: true,
      sortAccessor: (r) => (r.name ?? '').toLowerCase(),
      exportValue: (r) => r.name ?? '',
      render: (r) => (
        <p className="m-0 max-w-[240px] truncate font-semibold text-[color:var(--color-ink-strong)]" title={r.name ?? ''}>
          {r.name || <Muted>Unnamed company</Muted>}
        </p>
      ),
    },
    {
      key: 'trend',
      header: 'Trend',
      subheader: trendHeaderNote(slot),
      sortable: false,
      minWidth: 130,
      exportValue: (r) => {
        const t = trendInWords(r.trend, slot)
        return t.sub ? `${t.text} · ${t.sub}` : t.text
      },
      render: (r) => <TrendCell trend={r.trend} slot={slot} status={r.status} />,
    },
    {
      key: 'loads',
      header: 'Page loads',
      subheader: 'opens · people',
      align: 'right',
      minWidth: 120,
      sortAccessor: (r) => num(r.loads) ?? 0,
      exportValue: (r) => num(r.loads) ?? 0,
      render: (r) => (
        <div className="inline-flex flex-col items-end">
          <b className="tabular-nums text-[color:var(--color-ink-strong)]">{formatNumber(num(r.loads) ?? 0)}</b>
          {num(r.users) != null ? <span className="text-xs text-[color:var(--color-ink-muted)]">{countOf(r.users, 'person', 'people')}</span> : null}
        </div>
      ),
    },
    msColumn('avgMs', 'Average open', 'Avg · all opens ÷ opens', 'Every open’s time added up, divided by the number of opens.'),
    {
      key: 'p95Ms',
      header: 'Slowest 5 of 100 (P95)',
      subheader: 'the colour uses this',
      align: 'right',
      minWidth: 160,
      sortAccessor: (r) => num(r.p95Ms) ?? -1,
      exportValue: (r) => exportMs(r.p95Ms),
      render: (r) => <P95Cell row={r} limits={limits} />,
    },
    msColumn('p99Ms', 'Slowest 1 of 100 (P99)', 'the worst opens', '99 of every 100 opens finished within this — the worst case.'),
    {
      key: 'failedRate',
      header: 'Errors',
      subheader: 'opens with a failed request',
      headerTitle: ERRORS_TITLE,
      align: 'right',
      minWidth: 130,
      sortAccessor: (r) => num(r.failedRate) ?? -1,
      exportValue: (r) => (num(r.failedRate) == null ? '' : formatPercent(r.failedRate)),
      render: (r) => <ErrorsCell value={r.failedRate} limits={limits} />,
    },
    {
      key: 'slowestPage',
      header: 'Slowest page',
      subheader: 'by P95',
      sortable: false,
      minWidth: 200,
      exportValue: (r) => (r.slowestPage?.route ? `${r.slowestPage.route} (${formatMs(r.slowestPage.p95Ms)})` : ''),
      render: (r) =>
        r.slowestPage?.route ? (
          <div className="min-w-0 max-w-[220px]">
            <p className="m-0 truncate font-mono text-xs text-[color:var(--color-ink)]" title={r.slowestPage.route}>
              {r.slowestPage.route}
            </p>
            <p className="m-0 text-xs text-[color:var(--color-ink-muted)]">{formatMs(r.slowestPage.p95Ms)} P95</p>
          </div>
        ) : (
          <Muted>—</Muted>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      pinnedRight: true,
      minWidth: 140,
      sortable: false,
      exportValue: (r) => STATUS_LABEL[r.status] ?? 'Not judged',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <PerfStatusBadge status={r.status} />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onNarrow(r)
            }}
            aria-label={`Narrow every tab to ${r.name ?? 'this company'}`}
            title="Narrow every tab to this company"
            className="inline-flex min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-accent-strong)]"
          >
            <Filter size={15} />
          </button>
        </span>
      ),
    },
  ]
}
